import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ReactMarkdown from 'react-markdown';
import {
  MessageSquare, Send, Clock, CheckCircle, AlertCircle, Loader2,
  UserCheck, Eye, StickyNote, Filter, ChevronRight, Search, X
} from 'lucide-react';
import { API } from '@/App';

const SUBJECTS = [
  { id: 'Question scientifique', icon: '🔬', color: '#059669' },
  { id: 'Adhésion & paiement', icon: '💳', color: '#d97706' },
  { id: 'Annuaire / ma fiche', icon: '📋', color: '#35AFE4' },
  { id: 'Problème technique', icon: '🔧', color: '#dc2626' },
  { id: 'Autre', icon: '💬', color: '#64748b' },
];

const STATUS_MAP = {
  open: { label: 'Ouvert', color: '#059669', bg: '#ecfdf5', icon: AlertCircle },
  in_progress: { label: 'En cours', color: '#d97706', bg: '#fffbeb', icon: Clock },
  resolved: { label: 'Résolu', color: '#64748b', bg: '#f1f5f9', icon: CheckCircle },
};

const AdminMessagingPage = () => {
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({});
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEnd = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { loadAll(); }, [filterStatus, filterSubject]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSubject) params.append('subject', filterSubject);
      const [convRes, statsRes] = await Promise.all([
        axios.get(`${API}/messaging/conversations?${params}`, { headers }),
        axios.get(`${API}/messaging/stats`, { headers }),
      ]);
      setConversations(convRes.data);
      setStats(statsRes.data);
    } catch { }
    setLoading(false);
  };

  const openConversation = async (conv) => {
    try {
      const res = await axios.get(`${API}/messaging/conversations/${conv.id}`, { headers });
      setActiveConv(res.data.conversation);
      setMessages(res.data.messages);
      loadAll();
    } catch { }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeConv) return;
    setSending(true);
    try {
      await axios.post(`${API}/messaging/conversations/${activeConv.id}/messages`, {
        content: replyText.trim(),
        is_internal_note: isNote,
      }, { headers });
      setReplyText('');
      setIsNote(false);
      const res = await axios.get(`${API}/messaging/conversations/${activeConv.id}`, { headers });
      setMessages(res.data.messages);
      setActiveConv(res.data.conversation);
      loadAll();
    } catch { }
    setSending(false);
  };

  const changeStatus = async (status) => {
    if (!activeConv) return;
    try {
      await axios.put(`${API}/messaging/conversations/${activeConv.id}/status`, { status }, { headers });
      setActiveConv(prev => ({ ...prev, status }));
      loadAll();
    } catch { }
  };

  const assignToMe = async () => {
    if (!activeConv) return;
    try {
      const res = await axios.put(`${API}/messaging/conversations/${activeConv.id}/assign`, {}, { headers });
      setActiveConv(prev => ({ ...prev, assigned_to: res.data.assigned_to, assigned_name: res.data.assigned_name, status: 'in_progress' }));
      loadAll();
    } catch { }
  };

  const getSubjectInfo = (subj) => SUBJECTS.find(s => s.id === subj) || SUBJECTS[4];

  const filtered = searchQuery
    ? conversations.filter(c =>
        c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  return (
    <AdminLayout>
      <div className="flex h-[calc(100vh-105px)]" data-testid="admin-messaging-page">
        {/* Left panel — Conversation list */}
        <div className={`${activeConv ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[380px] bg-white border-r border-gray-100`}>
          {/* Stats bar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/50">
            {[
              { key: 'open', label: 'Ouverts', count: stats.open || 0, color: '#059669' },
              { key: 'in_progress', label: 'En cours', count: stats.in_progress || 0, color: '#d97706' },
              { key: 'unread', label: 'Non lus', count: stats.unread || 0, color: '#dc2626' },
              { key: 'resolved', label: 'Résolus', count: stats.resolved || 0, color: '#64748b' },
            ].map(s => (
              <button key={s.key}
                onClick={() => setFilterStatus(filterStatus === s.key ? '' : (s.key === 'unread' ? '' : s.key))}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-all ${
                  filterStatus === s.key ? 'text-white' : 'hover:bg-white'
                }`}
                style={filterStatus === s.key ? { backgroundColor: s.color } : { color: s.color }}
                data-testid={`filter-${s.key}`}>
                <span className="text-base font-bold">{s.count}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Search + Subject filter */}
          <div className="px-3 py-2 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher..." 
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#35AFE4]"
                data-testid="messaging-search" />
            </div>
            <div className="flex items-center gap-1 overflow-x-auto">
              <button onClick={() => setFilterSubject('')}
                className={`px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap ${!filterSubject ? 'bg-[#1e3a5f] text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                Tous
              </button>
              {SUBJECTS.map(s => (
                <button key={s.id} onClick={() => setFilterSubject(filterSubject === s.id ? '' : s.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap ${filterSubject === s.id ? 'text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                  style={filterSubject === s.id ? { backgroundColor: s.color } : {}}>
                  <span>{s.icon}</span> {s.id.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-sm text-gray-400">Aucune conversation</div>
            ) : (
              filtered.map(conv => {
                const subj = getSubjectInfo(conv.subject);
                const status = STATUS_MAP[conv.status] || STATUS_MAP.open;
                const isActive = activeConv?.id === conv.id;
                return (
                  <button key={conv.id} onClick={() => openConversation(conv)}
                    data-testid={`admin-conv-${conv.id}`}
                    className={`w-full flex items-start gap-2.5 px-3 py-3 text-left border-b border-gray-50 transition-colors ${
                      isActive ? 'bg-[#35AFE4]/5 border-l-2 border-l-[#35AFE4]' : 'hover:bg-gray-50'
                    }`}>
                    <span className="text-lg flex-shrink-0 mt-0.5">{subj.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-bold text-gray-900 truncate">{conv.title}</p>
                        {conv.unread_admin > 0 && (
                          <span className="min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold flex-shrink-0">
                            {conv.unread_admin}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">{conv.user_name} · {conv.user_email}</p>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">{conv.last_message_preview}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: status.color, backgroundColor: status.bg }}>
                        {status.label}
                      </span>
                      <span className="text-[9px] text-gray-400">
                        {new Date(conv.last_message_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </span>
                      {conv.assigned_name && (
                        <span className="text-[9px] text-[#35AFE4] font-medium">{conv.assigned_name}</span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel — Conversation detail */}
        <div className={`${activeConv ? 'flex' : 'hidden lg:flex'} flex-col flex-1 bg-white`}>
          {!activeConv ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <MessageSquare size={40} className="mb-3 opacity-30" />
              <p className="text-sm">Sélectionnez une conversation</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="border-b border-gray-100 px-4 py-3 flex items-center gap-3">
                <button onClick={() => { setActiveConv(null); setMessages([]); }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 lg:hidden">
                  <X size={18} />
                </button>
                <span className="text-xl">{getSubjectInfo(activeConv.subject).icon}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{activeConv.title}</h3>
                  <p className="text-[11px] text-gray-500">
                    {activeConv.user_name} · {activeConv.subject}
                    {activeConv.assigned_name && <span className="ml-2 text-[#35AFE4]">→ {activeConv.assigned_name}</span>}
                  </p>
                </div>
                {/* Action buttons */}
                <div className="flex items-center gap-1.5">
                  {!activeConv.assigned_to && (
                    <button onClick={assignToMe}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#35AFE4] text-white text-[11px] font-medium hover:bg-[#2a9cd4] transition-colors"
                      data-testid="assign-to-me-btn">
                      <UserCheck size={13} /> Je m'en occupe
                    </button>
                  )}
                  {activeConv.status !== 'resolved' && (
                    <button onClick={() => changeStatus('resolved')}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-medium hover:bg-gray-200 transition-colors"
                      data-testid="resolve-btn">
                      <CheckCircle size={13} /> Résoudre
                    </button>
                  )}
                  {activeConv.status === 'resolved' && (
                    <button onClick={() => changeStatus('open')}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 text-[11px] font-medium hover:bg-green-100 transition-colors"
                      data-testid="reopen-btn">
                      <AlertCircle size={13} /> Rouvrir
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(msg => {
                  const isAdmin = msg.sender_role === 'admin';
                  const isNote = msg.is_internal_note;
                  return (
                    <div key={msg.id}
                      className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                      data-testid={`admin-msg-${msg.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                        isNote
                          ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-br-md'
                          : isAdmin
                            ? 'bg-[#35AFE4] text-white rounded-br-md'
                            : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}>
                        {isNote && (
                          <div className="flex items-center gap-1 text-[10px] font-bold text-amber-600 mb-1">
                            <StickyNote size={10} /> Note interne
                          </div>
                        )}
                        <p className="text-[10px] font-bold mb-1 opacity-70">
                          {msg.sender_name}{isAdmin ? ' · Équipe' : ''}
                        </p>
                        <div className={isAdmin && !isNote ? '' : 'prose prose-sm max-w-none'}>
                          {isAdmin && !isNote ? msg.content : <ReactMarkdown>{msg.content}</ReactMarkdown>}
                        </div>
                        <p className={`text-[9px] mt-1 ${isNote ? 'text-amber-400' : isAdmin ? 'text-white/50' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEnd} />
              </div>

              {/* Reply input */}
              <div className="border-t border-gray-100 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setIsNote(false)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${!isNote ? 'bg-[#35AFE4] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                    data-testid="reply-mode-btn">
                    Réponse au membre
                  </button>
                  <button onClick={() => setIsNote(true)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${isNote ? 'bg-amber-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                    data-testid="note-mode-btn">
                    <StickyNote size={11} /> Note interne
                  </button>
                </div>
                <div className="flex items-end gap-2">
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder={isNote ? 'Note interne (invisible pour le membre)...' : 'Répondre au membre...'}
                    rows={2}
                    className={`flex-1 resize-none rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      isNote ? 'border-amber-200 focus:ring-amber-300/30 bg-amber-50/50' : 'border-gray-200 focus:ring-[#35AFE4]/30'
                    }`}
                    data-testid="admin-reply-input" disabled={sending} />
                  <button onClick={sendReply} disabled={!replyText.trim() || sending}
                    className={`p-2.5 rounded-xl text-white disabled:opacity-40 transition-colors ${
                      isNote ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#35AFE4] hover:bg-[#2a9cd4]'
                    }`}
                    data-testid="admin-reply-send-btn">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessagingPage;
